import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ReviewFormData } from '../../types';
import { reviewAPI } from '../../services/api';
import StarRating from './StarRating';
import './ReviewForm.css';

interface ReviewFormProps {
    productId: number;
    onReviewSubmitted?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ productId, onReviewSubmitted }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const isAuthenticated = !!localStorage.getItem('authTokens');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!isAuthenticated) {
            Swal.fire({
                title: t('reviews.loginRequired'),
                text: t('reviews.loginToReview'),
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: t('auth.login'),
                cancelButtonText: t('common.cancel'),
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/login');
                }
            });
            return;
        }

        if (rating === 0) {
            setError(t('reviews.ratingRequired'));
            return;
        }

        if (comment.trim().length < 10) {
            setError(t('reviews.commentTooShort'));
            return;
        }

        try {
            setSubmitting(true);
            const reviewData: ReviewFormData = {
                product: productId,
                rating,
                comment: comment.trim(),
            };

            await reviewAPI.createReview(reviewData);

            Swal.fire({
                title: t('reviews.thankYou'),
                text: t('reviews.pendingApproval'),
                icon: 'success',
                confirmButtonText: t('common.ok'),
            });

            // Reset form
            setRating(0);
            setComment('');

            if (onReviewSubmitted) {
                onReviewSubmitted();
            }
        } catch (err: any) {
            console.error('Error submitting review:', err);
            console.error('Error response:', err.response);
            console.error('Error data:', err.response?.data);

            let errorMessage = '';

            if (err.response?.status === 400 && err.response?.data) {
                const errorData = err.response.data;
                if (errorData.non_field_errors) {
                    errorMessage = errorData.non_field_errors[0];
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else {
                    errorMessage = JSON.stringify(errorData);
                }
            } else if (err.response?.status === 401) {
                errorMessage = 'Please login to submit a review';
            } else {
                errorMessage = t('reviews.submitError');
            }

            // Show alert for debugging
            alert('Review Submission Error:\n' + errorMessage);
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="review-form-login-prompt">
                <p>{t('reviews.loginToReview')}</p>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate('/login')}
                >
                    {t('auth.login')}
                </button>
            </div>
        );
    }

    return (
        <form className="review-form" onSubmit={handleSubmit}>
            <h3 className="review-form-title">{t('reviews.writeReview')}</h3>

            <div className="form-group">
                <label className="form-label">{t('reviews.yourRating')} *</label>
                <StarRating
                    rating={rating}
                    onChange={setRating}
                    size="large"
                />
            </div>

            <div className="form-group">
                <label className="form-label" htmlFor="review-comment">
                    {t('reviews.yourReview')} *
                </label>
                <textarea
                    id="review-comment"
                    className="form-textarea"
                    rows={5}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('reviews.commentPlaceholder')}
                    disabled={submitting}
                />
                <span className="form-hint">
                    {comment.length}/500 {t('reviews.characters')}
                </span>
            </div>

            {error && (
                <div className="form-error">
                    {error}
                </div>
            )}

            <button
                type="submit"
                className="btn btn-primary submit-review-btn"
                disabled={submitting || rating === 0 || comment.trim().length < 10}
            >
                {submitting ? t('reviews.submitting') : t('reviews.submitReview')}
            </button>
        </form>
    );
};

export default ReviewForm;
